import { extendObservable, action, computed } from 'mobx'
import _ from 'lodash'
import humps from 'lodash-humps'

import { REFRESH_RATE } from '@/constants'
import SentryReplay from '@/SentryReplay'
import { fromUTCToLocale, toSnakeCase } from '@/utils/helpers'
import request from '@/utils/request'

import Instance from './models/instance'

const initialState = {
  session: null,
  courses: null,
  coursesSearch: '',
  coursesTotal: 0,
  instance: new Instance(),
  instructors: null,
  instructorsSearch: '',
  instructorsTotal: 0,
  isCopyingInstance: false,
  isCourseNotFound: false,
  isLoadingCourseInstructors: false,
  isLoadingCourses: false,
  isLoadingInstructors: false,
  isLoadingEnrollLearners: false,
  isLoadingEnrollUsers: false,
  isLoadingNotifications: false,
  isLoadingOverview: false,
  isLoadingReportExportURL: false,
  isLoadingUnreadMessagesCount: false,
  isLoadingUsers: false,
  isRefreshingNotifications: false,
  loadNotificationsError: null,
  notifications: [],
  orgLearners: null,
  orgLearnersTerm: '',
  users: null,
  usersSearch: '',
  usersTotal: 0,
  errorLoadingCourses: null,
}

const __postEnrollUsers = (orgId, users, password) => {
  return request.post(`/admin/enrollment/users/${orgId}`, {
    password,
    users: users.map(({ email, firstName, lastName }) => ({
      email: email.trim(),
      first_name: (firstName && firstName.trim()) || '',
      last_name: (lastName && lastName.trim()) || '',
    })),
  })
}

export default class AdminStore {
  constructor(appStore) {
    this.appStore = appStore
    extendObservable(this, { ...initialState })
  }

  // reset state as stores are singletons of global state
  @action reset() {
    this.stopLoadUnreadMessagesCountInterval()
    extendObservable(this, { ...initialState })
  }

  @action init(session) {
    this.session = session
  }

  @action copyInstance(courseId, courseName, integrationType, ssoEndpoint) {
    const POST = {
      course_title: courseName,
    }
    const errors = {}
    if (!courseName) {
      errors['courseName'] = ['Field may not be empty.']
    }
    if (!integrationType) {
      errors['integrationType'] = ['Field may not be empty.']
    } else if (integrationType === 'ssoEndpoint' && !ssoEndpoint) {
      errors['ssoEndpoint'] = ['Field may not be empty.']
    }
    if (Object.keys(errors).length) {
      return Promise.reject(new Error(JSON.stringify(errors)))
    } else {
      if (integrationType === 'ssoEndpoint' && ssoEndpoint) {
        POST['sso_endpoint'] = ssoEndpoint
      }
      this.isCopyingInstance = true
      return request
        .post(`/admin/${courseId}/copy`, POST)
        .then(this.handleCopyInstance)
        .catch(this.handleCopyInstanceError)
    }
  }
  @action handleCopyInstance = () => {
    this.isCopyingInstance = false
    return Promise.resolve()
  }
  @action handleCopyInstanceError = (error) => {
    const { response } = error
    SentryReplay.captureException(error)
    this.isCopyingInstance = false
    return Promise.reject(
      response && response.data && response.data.errors
        ? new Error(JSON.stringify(humps(response.data.errors)))
        : new Error(
            JSON.stringify({
              confirm: ['There was an error while copying the course.'],
            }),
          ),
    )
  }

  @action loadCourses(options = {}) {
    const { filters } = options
    const { term } = filters || { term: '' }
    this.isLoadingCourses = true
    this.coursesSearch = term
    this.errorLoadingCourses = null
    return request
      .get('/admin/', {
        params: {
          ...filters,
          d: Date.now(),
        },
      })
      .then((response) => this.handleLoadCourses(response, term))
      .catch(this.handleLoadCoursesError)
  }
  @action handleLoadCourses = (response, term) => {
    const { data } = response
    const { courses, coursesTotal } = humps(data)
    if (term === this.coursesSearch) {
      this.courses = courses
      this.coursesTotal = coursesTotal
      this.isLoadingCourses = false
    }
  }
  @action handleLoadCoursesError = (error) => {
    SentryReplay.captureException(error)
    this.errorLoadingCourses = 'Failed to load courses.'
    this.isLoadingCourses = false
  }

  @action loadOverview = ({ courseId } = {}) => {
    this.isLoadingOverview = true
    return request
      .get(`/reporting/${courseId}/overview`, {
        params: {
          d: Date.now(),
        },
      })
      .then((response) => this.handleLoadOverview(response))
      .catch(this.handleLoadOverviewError)
  }
  @action handleLoadOverview = (response) => {
    const { data } = response
    this.overviewData = humps(data)
    this.isLoadingOverview = false
  }
  @action handleLoadOverviewError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingOverview = false
  }

  @action loadUserOrgs() {
    this.isLoadingUserOrgs = true
    return request
      .get(`/admin/orgs`, { params: { d: Date.now() } })
      .then(this.handleLoadUserOrgs)
      .catch(this.handleLoadUserOrgsError)
  }
  @action handleLoadUserOrgs = (response) => {
    const { data } = response
    const { orgs } = humps(data)
    return orgs
  }
  @action handleLoadUserOrgsError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingUserOrgs = false
    return Promise.reject(error)
  }

  @action loadOrgLearners(orgId, options = {}) {
    const { filters, sorting } = options
    const { term } = filters || { term: '' }
    this.isLoadingOrgLearners = true
    this.orgLearnersTerm = term
    return request
      .get(`/admin/${orgId}/learners/`, {
        params: {
          ...filters,
          ...toSnakeCase(sorting || {}),
          d: Date.now(),
        },
      })
      .then((response) => this.handleLoadOrgLearners(response, term))
      .catch((error) => this.handleLoadOrgLearnersError(error, term))
  }
  @action handleLoadOrgLearners = (response, term = '') => {
    const { data } = response
    const { learners, total } = humps(data)
    if (this.orgLearnersTerm === term) {
      this.orgLearners = learners
      this.orgLearnersTotal = total
      this.isLoadingOrgLearners = false
    }
  }
  @action handleLoadOrgLearnersError = (error, term = '') => {
    SentryReplay.captureException(error)
    this.isLoadingOrgLearners = false
    if (this.orgLearnersTerm === term) {
      return Promise.reject(error)
    }
  }

  @action loadCourseInstructors(courseId) {
    this.isLoadingCourseInstructors = true
    return request
      .get(`/admin/${courseId}/instructors/`, { params: { d: Date.now() } })
      .then(this.handleLoadCourseInstructors)
      .catch(this.handleLoadCourseInstructorsError)
  }
  @action handleLoadCourseInstructors = (response) => {
    const { data } = response
    const { instructors } = humps(data)
    this.isLoadingCourseInstructors = false
    return instructors || []
  }
  @action handleLoadCourseInstructorsError = (error) => {
    this.isLoadingCourseInstructors = false
    return Promise.reject(error)
  }

  // eslint-disable-next-line no-unused-vars
  @action enrollLearners = (courseId, instructorId, learnerIds) => {
    this.isLoadingEnrollLearners = true
    return request
      .post(
        `/admin/enrollment/${courseId}`,
        toSnakeCase({
          instructorId,
          learnerIds,
        }),
      )
      .then(this.handleEnrollLearners)
      .catch(this.handleEnrollLearnersError)
  }
  @action handleEnrollLearners = (response) => {
    this.isLoadingEnrollLearners = false
    return response
  }
  @action handleEnrollLearnersError = (error) => {
    this.isLoadingEnrollLearners = false
    return Promise.reject(error)
  }

  @action enrollUsers = (orgId, users, password) => {
    const chunks = _.chunk(users, 25)
    this.isLoadingEnrollUsers = true
    return Promise.all(
      chunks.map((chunk) => __postEnrollUsers(orgId, chunk, password)),
    )
      .then(this.handleEnrollUsers)
      .catch(this.handleEnrollUsersError)
  }
  @action handleEnrollUsers = (response) => {
    this.isLoadingEnrollUsers = false
    return response
  }
  @action handleEnrollUsersError = (error) => {
    this.isLoadingEnrollUsers = false
    return Promise.reject(error)
  }

  @action loadNotifications(isRefresh) {
    if (isRefresh) {
      this.isRefreshingNotifications = true
    } else {
      this.isLoadingNotifications = true
    }
    this.loadNotificationsError = null
    return request
      .get('/admin/pending-tasks-notifications', {
        params: {
          d: Date.now(),
        },
      })
      .then(this.handleLoadNotification)
      .catch(this.handleLoadNotificationError)
  }
  @action handleLoadNotification = (response) => {
    const { data } = response
    const { notifications } = data
    // update dates to locale
    this.notifications = humps(notifications).map((notification) => {
      const { addedAt, completed } = notification
      notification.addedAt = fromUTCToLocale(addedAt)
      notification.completed = completed ? fromUTCToLocale(completed) : null
      return notification
    })
    this.isLoadingNotifications = false
    this.isRefreshingNotifications = false
  }
  @action handleLoadNotificationError = (error) => {
    SentryReplay.captureException(error)
    this.loadNotificationsError = error
    this.isLoadingNotifications = false
    this.isRefreshingNotifications = false
  }

  @action loadSettings({ courseId }) {
    this.isLoadingSettings = true
    this.loadSettingsError = null
    return request
      .get(`/admin/${courseId}/get-course-settings`, {
        params: {
          d: Date.now(),
        },
      })
      .then(this.handleLoadSettings)
      .catch(this.handleLoadSettingsError)
  }
  @action handleLoadSettings = (response) => {
    const { data } = response
    const { courseSetting } = humps(data)
    this.settings = courseSetting
    this.isLoadingSettings = false
    return this.settings
  }
  @action handleLoadSettingsError = (error) => {
    SentryReplay.captureException(error)
    this.loadSettingsError = error
    this.isLoadingSettings = false
  }
  @action updateSettings({ courseId, settings }) {
    this.isUpdatingSettings = true
    this.updateSettingsError = null
    const {
      learningMasteryRequirements,
      learningMode,
      memoryBoosterMode,
      snapshot,
    } = settings
    const POST = {
      learner_dashboard: learningMasteryRequirements,
      memory_booster_settings: memoryBoosterMode,
      required_mastery_level: learningMode,
    }
    if (snapshot !== null) {
      POST['snapshot'] = snapshot
    }
    return request
      .post(`/admin/${courseId}/update-course-settings`, POST)
      .then(this.handleUpdateSettings)
      .catch(this.handleUpdateSettingsError)
  }
  @action handleUpdateSettings = () => {
    this.isUpdatingSettings = false
    return Promise.resolve()
  }
  @action handleUpdateSettingsError = (error) => {
    SentryReplay.captureException(error)
    this.updateSettingsError = 'Error updating course settings'
    this.isUpdatingSettings = false
    return Promise.reject(new Error('Error updating course settings'))
  }

  @action loadUsers(options = {}) {
    const { filters } = options
    const { term } = filters || { term: '' }
    this.isLoadingUsers = true
    this.usersSearch = term
    return request
      .get('/admin/users', {
        params: {
          ...filters,
          d: Date.now(),
        },
      })
      .then((response) => this.handleLoadUsers(response, term))
      .catch(this.handleLoadUsersError)
  }
  @action handleLoadUsers = (response, term = '') => {
    const { data } = response
    const { data: users, total: usersTotal } = humps(data)
    if (term === this.usersSearch) {
      this.users = users || []
      this.usersTotal = usersTotal
      this.isLoadingUsers = false
    }
  }
  @action handleLoadUsersError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingUsers = false
  }

  @action loadInstructorsList(options = {}) {
    const { filters } = options
    const { term } = filters || { term: '' }
    this.isLoadingInstructors = true
    this.instructorsSearch = term
    return request
      .get('/admin/instructors', {
        params: {
          ...filters,
          d: Date.now(),
        },
      })
      .then((response) => this.handleLoadInstructors(response, term))
      .catch(this.handleLoadInstructorsError)
  }
  @action handleLoadInstructors = (response, term = '') => {
    const { data } = response
    const { instructors, total } = humps(data)
    if (term === this.instructorsSearch) {
      // keep null (feature off / not applicable) distinct from [] so the tab
      // can be hidden when the endpoint returns null
      this.instructors = instructors || null
      this.instructorsTotal = total || 0
      this.isLoadingInstructors = false
    }
  }
  @action handleLoadInstructorsError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingInstructors = false
  }

  @computed get coursesAsTableData() {
    if (!this.courses || this.courses.length === 0) return []
    return this.courses.map((c) => ({
      id: c.id,
      isAdmin: c.isAdmin,
      isDisabledMessaging: c.disableMessaging,
      isInstructor: c.isInstructor,
      items: [
        c.title,
        c.id,
        c.status === 'Suspended' && c.liveStatus !== 'Offline'
          ? c.status
          : c.liveStatus,
        c.learnersCount,
      ],
      liveStatus: c.liveStatus,
    }))
  }
  @computed get usersAsTableData() {
    if (!this.users || this.users.length === 0) return []
    return this.users.map((user) => ({
      courseId: user.courseId,
      courseTitle: user.courseTitle,
      email: user.email,
      firstName: user.firstName || '',
      id: user.id,
      items: [
        `${user.firstName || ''} ${user.lastName || ''}`,
        user.email,
        user.courseTitle,
        user.role
          ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`
          : 'None',
      ],
      lastName: user.lastName || '',
      role: { type: user.role },
      roleId: user.roleId,
      roleStatus: user.roleStatus,
    }))
  }
  @computed get instructorsAsTableData() {
    if (!this.instructors || this.instructors.length === 0) return []
    return this.instructors.map((instructor) => ({
      email: instructor.email,
      firstName: instructor.firstName || '',
      instructorAccessCode: instructor.instructorAccessCode,
      items: [
        instructor.firstName || '',
        instructor.lastName || '',
        instructor.email,
        instructor.instructorAccessCode,
      ],
      lastName: instructor.lastName || '',
    }))
  }

  @action resetInstance() {
    this.instance = new Instance()
  }
  @action loadInstance(instanceId) {
    this.resetInstance()
    return this.loadCourses({ filters: { course_id: instanceId } })
      .then(() => {
        const course =
          this.courses.filter(
            (course) => course.id === parseInt(instanceId),
          )[0] || null
        if (course) {
          const {
            adminsCount,
            contentfulId,
            endDate,
            instructorsCount,
            isAdmin,
            isInstructor,
            learnersCount,
            liveStatus,
            status,
            title,
          } = course
          this.instance = new Instance({
            id: instanceId,
            adminsCount: adminsCount,
            contentfulId: contentfulId,
            endDate: endDate,
            instructorsCount: instructorsCount,
            isAdmin: isAdmin,
            isInstructor: isInstructor,
            learnersCount: learnersCount,
            liveStatus: liveStatus,
            status: status,
            title: title,
          })
          this.isCourseNotFound = false
        } else {
          this.isCourseNotFound = true
        }
      })
      .catch((error) => {
        SentryReplay.captureException(error)
      })
  }
  @action loadReportExportURL = ({ courseId, expired = false }) => {
    this.isLoadingReportExportURL = true
    let params = { d: Date.now() }
    if (expired) {
      params['expired'] = 1
    }
    return request
      .get(`/reporting/${courseId}/export`, { params: params })
      .then(this.handleLoadReportExport)
      .catch(this.handleLoadReportExportError)
  }
  @action handleLoadReportExport = (response) => {
    this.isLoadingReportExportURL = false
    const { data } = response || {}
    const { course_url_v2: reportExportURL } = data || {}
    return { url: reportExportURL }
  }
  @action handleLoadReportExportError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingReportExportURL = false
    return { error: error.response }
  }

  @action loadUnreadMessagesCount() {
    if (!this.session) {
      if (this.loadUnreadMessagesCountIntervalId) {
        return this.stopLoadUnreadMessagesCountInterval()
      }
      return Promise.resolve()
    }
    const { courseId } = this.session
    this.isLoadingUnreadMessagesCount = true
    return request
      .get(`/messages/${courseId}/counts?d=${Date.now()}`)
      .then(this.handleLoadUnreadMessagesCount)
      .catch(this.handleLoadUnreadMessagesCountError)
  }
  @action handleLoadUnreadMessagesCount = (response) => {
    const { data } = response
    const result = humps(data)
    this.isLoadingUnreadMessagesCount = false
    return result
  }
  @action handleLoadUnreadMessagesCountError = (error) => {
    SentryReplay.captureException(error)
    this.isLoadingUnreadMessagesCount = false
    return false
  }

  @action startLoadUnreadMessagesCountInterval = (callback) => {
    if (this.loadUnreadMessagesCountIntervalId) {
      return
    }
    this.loadUnreadMessagesCountIntervalId = setInterval(() => {
      this.loadUnreadMessagesCount().then((unreadMessagesCount) => {
        if (callback) {
          callback(unreadMessagesCount)
        }
      })
    }, REFRESH_RATE * 12) // 5000 * 12 === 60000 === 1min
    return this.loadUnreadMessagesCountIntervalId
  }
  @action stopLoadUnreadMessagesCountInterval = () => {
    clearInterval(this.loadUnreadMessagesCountIntervalId)
    this.loadUnreadMessagesCountIntervalId = null
    return this.loadUnreadMessagesCountIntervalId
  }
}
